// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as React from "react";
import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;

    // <xng-change>
    documentsTitle?: TypographyPresentationProps;
    documentLinkMore?: LinkPresentationProps;
    documentLinkLess?: LinkPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<OwnProps> = ({ product: { documents, productTitle, productNumber } }) => {
    const [expanded, setExpanded] = React.useState(false);
    const { xGoogleAnalytics } = useAppSelector(state => {
        return {
            xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        };
    }, shallowEqual);

    if (!documents || documents.length === 0) {
        return null;
    }
    const showMore = () => {
        setExpanded(true);
    };
    const showLess = () => {
        setExpanded(false);
    };

    // XNG-Changes: Google Analytics
    const gaDocumentClickHandler = (document: DocumentModel) => {
        if (xGoogleAnalytics.pdpDocumentClickEvent && document.filePath) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.pdpDocumentClickEventName,
                document_id: document.id,
                document_name: document.name,
                item_id: productNumber,
                item_name: productTitle,
                destination: document.filePath,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    const documentTemp = (document: DocumentModel) => {
        return (
            <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                <Link
                    href={document.filePath}
                    onClick={() => gaDocumentClickHandler(document)}
                    target="_new"
                    {...styles.documentLink}
                    data-test-selector={`productDetails_${document.id}`}
                >
                    <IconMemo {...styles.documentIcon} />
                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                </Link>
            </GridItem>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.documentsTitle}>{translate("Documents")}</Typography>
            <GridContainer {...styles.container}>
                {documents.map((document, index) => {
                    if (index > 2 && !expanded) {
                        return;
                    }
                    return documentTemp(document);
                })}
            </GridContainer>
            <GridContainer {...styles.container} className="for-print">
                {documents.map(document => {
                    return documentTemp(document);
                })}
            </GridContainer>
            {documents.length > 3 && (
                <StyledWrapper
                    css={css`
                        margin-top: 5px;
                        margin-bottom: 10px;
                    `}
                >
                    {!expanded ? (
                        <Link {...styles.documentLinkMore} onClick={showMore} aria-label={translate("Show More")}>
                            {translate("Show More")}
                        </Link>
                    ) : (
                        <Link {...styles.documentLinkLess} onClick={showLess} aria-label={translate("Show Less")}>
                            {translate("Show Less")}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
